import { InjectionToken, Signal } from '@angular/core';
import type { BoardStepPosition } from '../../state/board.panel.stepping';
import type { BoardPanelExpand } from './board.panel.expand';

/** What a refresh should reach. */
export type BoardPanelRefreshScope = 'record' | 'collection';

export interface BoardPanelRowPatch {
  rowId: string;
  patch: Record<string, unknown>;
}

/**
 * How content mounted inside a board panel acts on the panel and the board around it.
 *
 * Content must not reach into `EntityBoardStore` directly: the same surface also mounts in a detail
 * pane, a dialog, and later a frame, where no board exists. Injecting this — always
 * `{ optional: true }` — degrades cleanly in those places instead of throwing.
 *
 * An `InjectionToken` plus an interface rather than an abstract class: the value is built per mount,
 * so there is no injectable class to implement, and the token's generic already enforces the shape
 * at the provider. Never cast at the provider — `as BoardPanelHost` or a `useFactory` returning
 * `any` defeats the only checking there is.
 */
export interface BoardPanelHost {

  /** Whether the panel is in layout (pinned) or floating over the board. */
  readonly $pinned: Signal<boolean>;

  /** Quickview offers pin. Preview is always pinned and has no control. Editors that own the title bar should render pin themselves and call `setPinned`. */
  readonly $pinAvailable: Signal<boolean>;

  /** Viewport can split. Editors disable their pin control when this is false. */
  readonly $canPin: Signal<boolean>;

  /** True = this panel draws title/pin/close. False = resizable box only; injectors render pin on the editor. */
  readonly $ownsHeader: Signal<boolean>;

  setPinned(pinned: boolean): void;

  /** The board's view reports a visible order holding the panel's record, and stepping is not disabled. */
  readonly $canStep: Signal<boolean>;

  /** 1-based position among the board's rows; the arrows stop at the loaded page's edges. */
  readonly $stepPosition: Signal<BoardStepPosition | null>;

  /** Asks the board to move focus; the board flushes the panel's edits first and may refuse. */
  step(direction: 1 | -1): void;

  /** ⤢ and ⤡: what expanding opens, and whether a popped-out dialog may dock back. */
  readonly $expand: Signal<BoardPanelExpand>;

  /** Asks the board to open the bigger view; a frame switch runs the same leave guard as stepping. */
  expand(): void;

  /** Asks the board to return a popped-out dialog to the side. */
  dock(): void;

  /** ↗ target; null hides the control. */
  readonly $recordPageUrl: Signal<string | null>;

  /** Opens the record page in a new tab, so the board and its unsaved edits stay where they are. */
  openRecordPage(): void;

  /** Explicit close. Later selections do not reopen the panel until the board's context changes. */
  close(): void;

  /** Update an identified display row from a confirmed save without reloading the tree. */
  patchRow(rowId: string, patch: Record<string, unknown>): void;

  /**
   * Show a confirmed record in place of the one the collection currently carries.
   *
   * `patchRow` addresses a rendered row by id and only reaches displays that own their rows (an SSRM
   * tree). This hands over the whole saved record instead, which is what a collection display
   * (kanban, card, list) needs to move a card between lanes without refetching the page.
   */
  upsertRecord(record: unknown): void;

  /**
   * Most mutations need no call at all: `EntityDataService` already invalidates `[entity, 'items']`
   * and `[entity, 'item', id]` on patch, save and delete, so the row behind the panel updates on its
   * own. This is for what falls outside that — a command endpoint that bypasses those mutations, or
   * board state that is not a query (segment counts).
   *
   * Takes no payload deliberately: passing the mutated record back would have the panel asserting
   * state the server has not confirmed, and would need merge rules for partial updates.
   */
  refresh(scope?: BoardPanelRefreshScope): void;
}

export const BOARD_PANEL_HOST = new InjectionToken<BoardPanelHost>('BoardPanelHost');
